// useFocusEntity — universal hook for the Audit Stream Widget drill-down.
//
// Reads ``?focus=<id>`` from the current location and, when the host
// page renders the row whose id matches, scrolls it into view and
// applies a 3-second pulse highlight.
//
// Usage (host page):
//
//   const { focusedId, registerRow } = useFocusEntity();
//   ...
//   {rows.map(r => (
//     <tr
//       key={r.id}
//       ref={registerRow(r.id)}
//       className={focusedId === r.id ? 'focus-highlight' : ''}
//     >...</tr>
//   ))}
//
// The hook handles:
//   • One-time scrollIntoView when the matching ref mounts.
//   • Auto-clear after `highlightMs` so the glow doesn't linger when
//     the user navigates around the page.
//   • Stripping the ?focus param from the URL after the highlight
//     fires (so a manual reload doesn't replay the animation).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const HIGHLIGHT_MS = 3000;

export function useFocusEntity({ highlightMs = HIGHLIGHT_MS } = {}) {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract `?focus=` from the URL only once per navigation; clear it
  // immediately so a subsequent re-render doesn't keep re-triggering.
  const focusedId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get('focus') || null;
  }, [location.search]);

  // Refs of every registered row so we can scroll the matching one in.
  const rowRefs = useRef(new Map());
  const [highlightedId, setHighlightedId] = useState(focusedId);
  const triggeredRef = useRef(new Set()); // ids we've already scrolled to

  const registerRow = useCallback((id) => (node) => {
    if (!id) return;
    if (node) {
      rowRefs.current.set(id, node);
    } else {
      rowRefs.current.delete(id);
    }
  }, []);

  // When focusedId becomes available AND the corresponding ref is mounted,
  // scroll + highlight, then clear after `highlightMs`.
  useEffect(() => {
    if (!focusedId) return;
    if (triggeredRef.current.has(focusedId)) return;

    // Poll the ref map for a short window — the row may render slightly
    // after the hook first sees the focus id (data fetch latency).
    let cancelled = false;
    const start = Date.now();
    const tryScroll = () => {
      if (cancelled) return;
      const node = rowRefs.current.get(focusedId);
      if (node) {
        triggeredRef.current.add(focusedId);
        setHighlightedId(focusedId);
        try {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {
          // older browsers
          node.scrollIntoView();
        }
        // Drop the ?focus param so a manual reload doesn't replay.
        const sp = new URLSearchParams(location.search);
        sp.delete('focus');
        const next = `${location.pathname}${sp.toString() ? `?${sp.toString()}` : ''}`;
        navigate(next, { replace: true });
        // Clear the highlight class after the pulse.
        const t = setTimeout(() => {
          if (!cancelled) setHighlightedId((cur) => (cur === focusedId ? null : cur));
        }, highlightMs);
        return () => clearTimeout(t);
      }
      if (Date.now() - start < 5000) {
        setTimeout(tryScroll, 150);
      }
    };
    const cleanup = tryScroll();
    return () => {
      cancelled = true;
      if (typeof cleanup === 'function') cleanup();
    };
  }, [focusedId, highlightMs, location, navigate]);

  return {
    focusedId: highlightedId,
    registerRow,
  };
}

export default useFocusEntity;
